/**
 * Workspace settings for the institute: branding (TL-060), parent payments
 * via Stripe Connect (TL-062), custom domains (TL-063) and the setup wizard
 * state (TL-064). Contract: tutorLoop-docs/14-API-CONTRACT.md, section 1.
 * Subscription lives in `subscription.ts`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { mapTenant, type Tenant, type TenantWire } from "./tenant";

/* ─────────────────────────────────────────── Branding */

export interface BrandingInput {
  displayName?: string;
  primaryColour?: string;
  accentColour?: string;
  emailSenderName?: string;
  replyToEmail?: string;
  supportEmail?: string;
  /** A new file, or `null` to clear. Leave undefined to keep. */
  logo?: File | null;
  logoMark?: File | null;
}

function brandingForm(input: BrandingInput): FormData {
  const fd = new FormData();
  const put = (k: string, v: string | undefined) => {
    if (v !== undefined) fd.append(k, v);
  };
  put("display_name", input.displayName);
  put("primary_colour", input.primaryColour);
  put("accent_colour", input.accentColour);
  put("email_sender_name", input.emailSenderName);
  put("reply_to_email", input.replyToEmail);
  put("support_email", input.supportEmail);
  // The contract clears an image with an empty string.
  if (input.logo !== undefined) fd.append("logo", input.logo ?? "");
  if (input.logoMark !== undefined) fd.append("logo_mark", input.logoMark ?? "");
  return fd;
}

export const brandingApi = {
  get: async (): Promise<Tenant> => mapTenant((await api.get<TenantWire>("/tenant/branding/")).data),
  save: async (input: BrandingInput): Promise<Tenant> => mapTenant((await api.patch<TenantWire>("/tenant/branding/", brandingForm(input))).data),
};

export function useBranding() {
  return useQuery({ queryKey: ["tenant", "branding"], queryFn: brandingApi.get, staleTime: 30_000 });
}

export function useSaveBranding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: brandingApi.save,
    onSuccess: (t) => qc.setQueryData(["tenant", "branding"], t),
  });
}

/* ─────────────────────────────────────────── Parent payments (Stripe Connect) */

export interface PaymentsStatus {
  connected: boolean;
  accountLabel: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
}

export const paymentsApi = {
  get: async (): Promise<PaymentsStatus> => {
    const r = (
      await api.get<{ connected: boolean; account_label: string | null; charges_enabled: boolean; payouts_enabled: boolean; details_submitted: boolean }>(
        "/tenant/payments/",
      )
    ).data;
    return {
      connected: r.connected,
      accountLabel: r.account_label,
      chargesEnabled: r.charges_enabled,
      payoutsEnabled: r.payouts_enabled,
      detailsSubmitted: r.details_submitted,
    };
  },
  connect: async (): Promise<string> => (await api.post<{ url: string }>("/tenant/payments/connect/", {})).data.url,
  disconnect: async (): Promise<void> => {
    await api.post<void>("/tenant/payments/disconnect/", {});
  },
};

export function usePayments(enabled = true) {
  return useQuery({ queryKey: ["tenant", "payments"], queryFn: paymentsApi.get, enabled, staleTime: 15_000 });
}

/** Sends the browser to Stripe's onboarding. */
export function useConnectStripe() {
  return useMutation({ mutationFn: paymentsApi.connect, onSuccess: (url) => window.location.assign(url) });
}

export function useDisconnectStripe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: paymentsApi.disconnect,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["tenant", "payments"] }),
  });
}

/* ─────────────────────────────────────────── Domains */

export interface DnsRecord {
  type: string;
  name: string;
  value: string;
}

export interface DomainDns extends DnsRecord {
  txt?: DnsRecord | null;
}

export interface TenantDomain {
  id: number;
  domain: string;
  isPrimary: boolean;
  isCustom: boolean;
  verifiedAt: string | null;
  dns: DomainDns | null;
}

interface DomainWire {
  id: number;
  domain: string;
  is_primary: boolean;
  is_custom: boolean;
  verified_at: string | null;
  dns?: DomainDns | null;
}

const mapDomain = (d: DomainWire): TenantDomain => ({
  id: d.id,
  domain: d.domain,
  isPrimary: d.is_primary,
  isCustom: d.is_custom,
  verifiedAt: d.verified_at,
  dns: d.dns ?? null,
});

export const domainsApi = {
  list: async () => (await api.get<DomainWire[]>("/tenant/domains/")).data.map(mapDomain),
  add: async (domain: string) => mapDomain((await api.post<DomainWire>("/tenant/domains/", { domain })).data),
  verify: async (id: number) => mapDomain((await api.post<DomainWire>(`/tenant/domains/${id}/verify/`, {})).data),
  makePrimary: async (id: number) => {
    await api.post<unknown>(`/tenant/domains/${id}/make-primary/`, {});
  },
  remove: async (id: number) => {
    await api.delete<void>(`/tenant/domains/${id}/`);
  },
};

const DOMAINS = ["tenant", "domains"] as const;

export function useDomains(enabled = true) {
  return useQuery({ queryKey: DOMAINS, queryFn: domainsApi.list, enabled });
}

function useDomainMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => void qc.invalidateQueries({ queryKey: DOMAINS }) });
}

export const useAddDomain = () => useDomainMutation(domainsApi.add);
export const useVerifyDomain = () => useDomainMutation(domainsApi.verify);
export const useMakePrimaryDomain = () => useDomainMutation(domainsApi.makePrimary);
export const useRemoveDomain = () => useDomainMutation(domainsApi.remove);

/* ─────────────────────────────────────────── Setup wizard */

export type SetupStep = "branding" | "tutors" | "students" | "payments";

export interface SetupState {
  steps: Record<SetupStep, boolean>;
  dismissed: boolean;
  complete: boolean;
}

export const setupApi = {
  get: async () => (await api.get<SetupState>("/tenant/setup/")).data,
  markStep: async ({ step, done }: { step: SetupStep; done: boolean }) => (await api.patch<SetupState>("/tenant/setup/", { step, done })).data,
  dismiss: async () => (await api.patch<SetupState>("/tenant/setup/", { dismissed: true })).data,
};

const SETUP = ["tenant", "setup"] as const;

export function useSetup(enabled = true) {
  return useQuery({ queryKey: SETUP, queryFn: setupApi.get, enabled, staleTime: 10_000 });
}

export function useMarkSetupStep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setupApi.markStep,
    onSuccess: (s) => {
      if (s && typeof s === "object" && "steps" in s) qc.setQueryData(SETUP, s);
      else void qc.invalidateQueries({ queryKey: SETUP });
    },
  });
}

export function useDismissSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setupApi.dismiss,
    onSuccess: (s) => {
      if (s && typeof s === "object" && "steps" in s) qc.setQueryData(SETUP, s);
      else void qc.invalidateQueries({ queryKey: SETUP });
    },
  });
}
